import { useEffect, useState } from 'react';
import Spinner from '@atlaskit/spinner';
import Textfield from '@atlaskit/textfield';
import { useFlags } from '@atlaskit/flag';
import PageHeader from 'shared/components/page-header/page-header';
import SharedButton from 'shared/components/buttons/button';
import DevToolsApiService from 'core/api/dev-tools/dev-tools-api.service';
import { IMailAllowlist } from 'core/api/dev-tools/dev-tools-api-interface';
import { showErrorFlag, showSuccessFlag } from 'core/utilities/flags-helper';
import DevToolsNav from './dev-tools-nav';

/**
 * Who dev is allowed to email.
 *
 * Outside production every send is checked against this list, so testing can
 * never mail a real team or supplier. Entries from the deploy workflow are
 * shown but fixed; entries added here take effect on the next send, without a
 * deploy.
 */

/** Mirrors the check on the server, so a typo is caught before saving. */
const isValidEntry = (entry: string) =>
  /^\*@[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(entry) || /^[^\s@*]+@[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(entry);

const MailAllowlist = () => {
  const flags = useFlags();
  const [allowlist, setAllowlist] = useState<IMailAllowlist>();
  const [loadError, setLoadError] = useState<string>();
  const [entries, setEntries] = useState<string[]>([]);
  const [draft, setDraft] = useState('');
  const [draftError, setDraftError] = useState<string>();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    DevToolsApiService.mailAllowlist()
      .then((loaded) => {
        setAllowlist(loaded);
        setEntries(loaded.managed);
      })
      .catch((error: Error) => setLoadError(error.message));
  }, []);

  const changed = allowlist ? entries.join(',') !== allowlist.managed.join(',') : false;

  const add = () => {
    const entry = draft.trim().toLowerCase();
    if (!entry) return;
    if (!isValidEntry(entry)) {
      setDraftError('Give a full address, or a whole domain written *@example.com.');
      return;
    }
    if (entries.includes(entry) || allowlist?.fixed.includes(entry)) {
      setDraftError('That is already on the list.');
      return;
    }
    setEntries((current) => [...current, entry]);
    setDraft('');
    setDraftError(undefined);
  };

  const save = async () => {
    setSaving(true);
    try {
      const saved = await DevToolsApiService.saveMailAllowlist(entries);
      setAllowlist(saved);
      setEntries(saved.managed);
      showSuccessFlag('Allowlist saved', 'It applies from the next email dev sends.', flags);
    } catch (error) {
      showErrorFlag('Could not save', (error as Error).message, flags);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PageHeader title='Email allowlist' subtitle='Who the development system is allowed to email.' />
      <DevToolsNav />

      <div className='mt-6 overflow-y-auto pb-10'>
        {loadError && <p className='body-01 text-red-700'>{loadError}</p>}
        {!allowlist && !loadError && (
          <div className='mt-10 flex justify-center'>
            <Spinner size={40} />
          </div>
        )}

        {allowlist && (
          <>
            <div className='mb-6 rounded border border-yellow-300 bg-yellow-50 p-3'>
              <p className='body-01 text-yellow-900'>
                {allowlist.testMode ? (
                  <>
                    <span className='font-semibold'>Test mode is on.</span> Email goes only to the addresses and domains
                    below, every subject starts <code>[TEST]</code>, and anything else is blocked — the request then
                    says why, in orange.
                  </>
                ) : (
                  <>
                    <span className='font-semibold'>Test mode is off</span>, so this list is not being used: mail goes
                    to anyone.
                  </>
                )}{' '}
                Sent from <code>{allowlist.from ?? 'nothing — mail is not configured'}</code>.
              </p>
            </div>

            <div className='rounded border p-5' data-testid='mail-allowlist-fixed'>
              <p className='body-02 font-semibold'>Set by the deploy</p>
              <p className='body-01 mt-1 text-gray-600'>
                From <code>SENDGRID_ALLOWED_RECIPIENTS</code> in the dev workflow. Always allowed; change them there.
              </p>
              <div className='mt-3 flex flex-wrap gap-2'>
                {allowlist.fixed.length ? (
                  allowlist.fixed.map((entry) => (
                    // Not a lozenge: it uppercases and truncates, and an address
                    // has to be read exactly.
                    <code key={entry} className='body-01 rounded border bg-gray-50 px-2 py-0.5'>
                      {entry}
                    </code>
                  ))
                ) : (
                  <p className='body-01 text-gray-600'>None.</p>
                )}
              </div>
            </div>

            <div className='mt-6 rounded border p-5' data-testid='mail-allowlist-managed'>
              <p className='body-02 font-semibold'>Added here</p>
              <p className='body-01 mt-1 text-gray-600'>
                A full address such as <code>someone@nhs.net</code>, or a whole domain written{' '}
                <code>*@d-hive.co.uk</code>. Takes effect from the next email once saved — no deploy needed.
              </p>

              {entries.length === 0 && <p className='body-01 mt-3 text-gray-600'>Nothing added yet.</p>}
              {entries.map((entry) => (
                <div key={entry} className='mt-2 flex items-center justify-between border-t pt-2'>
                  <code className='body-01'>{entry}</code>
                  <SharedButton
                    type='button'
                    appearance='subtle'
                    spacing='compact'
                    label='Remove'
                    onClick={() => setEntries((current) => current.filter((existing) => existing !== entry))}
                  />
                </div>
              ))}

              <div className='mt-4 flex items-start gap-2'>
                <div className='flex-1'>
                  <Textfield
                    value={draft}
                    placeholder='someone@nhs.net or *@nhs.net'
                    onChange={(event) => setDraft((event.target as HTMLInputElement).value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') add();
                    }}
                    testId='mail-allowlist-input'
                  />
                  {draftError && <p className='body-01 mt-1 text-red-700'>{draftError}</p>}
                </div>
                <SharedButton type='button' appearance='default' label='Add' onClick={add} />
              </div>

              <div className='mt-4 flex items-center gap-3'>
                <SharedButton
                  type='button'
                  appearance='primary'
                  label='Save'
                  isLoading={saving}
                  disabled={!changed}
                  onClick={save}
                  testId='mail-allowlist-save'
                />
                {changed && <span className='body-01 text-amber-800'>Unsaved changes</span>}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default MailAllowlist;
